import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle } from 'lucide-react';
import { AskMarkdown } from './AskMarkdown';
import { DocumentReader } from '../../word/api/DocumentReader';
import { ChatComposer } from './ChatComposer';
import { detectTextDirection } from '../../utils/textUtils';
import { directionForLanguage } from '../../i18n/config';
import {
  getReviewPartyContextInstruction,
  hasReviewPartyContextComplete,
} from '../../utils/reviewPartyContext';
import apiService from '../../services/api';
import { filesToAttachmentPayloads } from '../../utils/fileAttachments';
import { handleError } from '../../utils/errorHandling';
import './AskView.css';

type ChatTurn = { id: string; role: 'user' | 'assistant'; content: string };

/** Must match engine `TextProcessingService._DOCUMENT_QA_MAX_CHARS` */
const DOCUMENT_ASK_MAX_CHARS = 200_000;

function isWordApiAvailable(): boolean {
  return typeof Word !== 'undefined' && typeof Word.run === 'function';
}

export interface AskViewProps {
  /** When party context is missing, parent switches to Review in the same task pane. */
  onRequireReview?: () => void;
}

export const AskView: React.FC<AskViewProps> = ({ onRequireReview }) => {
  const { t, i18n } = useTranslation('taskpane');
  const chromeDir = directionForLanguage(i18n.language);
  const [documentText, setDocumentText] = React.useState('');
  const [docLoading, setDocLoading] = React.useState(true);
  const [docError, setDocError] = React.useState<string | null>(null);
  const [messages, setMessages] = React.useState<ChatTurn[]>([]);
  const [sending, setSending] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  const loadDocument = React.useCallback(async () => {
    if (!isWordApiAvailable()) {
      setDocError(t('ask.wordApiUnavailable'));
      setDocumentText('');
      setDocLoading(false);
      return;
    }
    setDocLoading(true);
    setDocError(null);
    try {
      const text = await DocumentReader.getFullDocumentText();
      setDocumentText(text || '');
      if (!text?.trim()) {
        setDocError(t('ask.emptyDocument'));
      }
    } catch (e) {
      setDocError(handleError(e));
      setDocumentText('');
    } finally {
      setDocLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void loadDocument();
  }, [loadDocument]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const handleSend = async (text: string, attachmentFiles: File[]): Promise<boolean> => {
    const trimmed = text.trim();
    if ((!trimmed && attachmentFiles.length === 0) || sending) return false;

    if (!documentText.trim()) {
      return false;
    }

    if (!hasReviewPartyContextComplete()) {
      onRequireReview?.();
      return false;
    }

    const userDisplay =
      trimmed ||
      (attachmentFiles.length
        ? t('ask.attached', { names: attachmentFiles.map((f) => f.name).join(', ') })
        : '');
    const userMsg: ChatTurn = { id: `u-${Date.now()}`, role: 'user', content: userDisplay };
    setMessages((prev) => [...prev, userMsg]);
    setSending(true);

    const prior = messages.map((m) => ({ role: m.role, content: m.content }));

    try {
      const textForApi =
        documentText.length > DOCUMENT_ASK_MAX_CHARS
          ? documentText.slice(0, DOCUMENT_ASK_MAX_CHARS)
          : documentText;
      const attachments =
        attachmentFiles.length > 0 ? await filesToAttachmentPayloads(attachmentFiles) : undefined;
      const questionForApi =
        trimmed ||
        (attachmentFiles.length
          ? 'Use the attached reference document(s) to answer in context of the open document.'
          : '');
      const res = await apiService.documentAsk(
        textForApi,
        questionForApi,
        prior,
        getReviewPartyContextInstruction(),
        attachments
      );
      if (!res.success || !res.data?.answer) {
        throw new Error(res.message || res.error || t('ask.noAnswer'));
      }
      setMessages((prev) => [
        ...prev,
        { id: `a-${Date.now()}`, role: 'assistant', content: res.data!.answer },
      ]);
      return true;
    } catch (e) {
      const msg = handleError(e);
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: t('ask.sendFailed', { detail: msg }),
        },
      ]);
      return true;
    } finally {
      setSending(false);
    }
  };

  const inputDisabled = docLoading || !documentText.trim() || sending;

  return (
    <div className="ask-view">
      <div className="ask-messages">
        {messages.length === 0 && (
          <div className="ask-empty" dir={chromeDir}>
            {docLoading ? (
              <p className="ask-empty-loading">{t('ask.readingDocument')}</p>
            ) : docError ? (
              <p className="ask-empty-error">{docError}</p>
            ) : (
              <>
                <MessageCircle className="ask-empty-icon" size={40} />
                <p>{t('ask.emptyTitle')}</p>
                <p className="ask-empty-hint">
                  {t('ask.emptyHint')}
                </p>
              </>
            )}
          </div>
        )}

        {messages.map((m) => {
          const dir = detectTextDirection(m.content);
          const isUser = m.role === 'user';
          return (
            <div key={m.id} className={`ask-bubble ask-bubble-${m.role}`}>
              <div className="ask-bubble-role">{isUser ? t('ask.you') : 'Courtyard'}</div>
              {isUser ? (
                <div
                  className="ask-bubble-content"
                  dir={dir}
                  style={{ textAlign: dir === 'rtl' ? 'right' : 'left' }}
                >
                  {m.content}
                </div>
              ) : (
                <AskMarkdown content={m.content} dir={dir} />
              )}
            </div>
          );
        })}

        {sending && (
          <div className="ask-bubble ask-bubble-assistant">
            <div className="ask-bubble-role">Courtyard</div>
            <div className="ask-typing">
              <span /><span /><span />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="ask-input-area" dir={chromeDir}>
        <ChatComposer
          value={draft}
          onChange={setDraft}
          onSubmit={async ({ text, attachments: att }) => {
            const trimmed = text.trim();
            if ((!trimmed && att.length === 0) || sending) return;
            const ok = await handleSend(trimmed, att);
            if (ok) setDraft('');
          }}
          disabled={inputDisabled}
          submitting={sending}
          placeholder={docLoading ? t('ask.loadingDocument') : t('ask.placeholder')}
          layout="sticky"
          label={
            messages.length === 0 ? (
              <div className="ag-composer-label">
                <MessageCircle size={14} aria-hidden />
                <span>{t('ask.label')}</span>
              </div>
            ) : undefined
          }
          followUp={messages.length > 0}
          submitTitle={t('ask.sendQuestion')}
          showAgentModeSelector={false}
        />
      </div>
    </div>
  );
};
